import React, { useState, useEffect, useCallback, useRef } from 'react';
import './index.css';

const debounce = (fn, delay) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), delay);
  };
};

const isOperator = (char) => ['+', '-', '*', '/'].includes(char);

const Calculator = ({ inputValue, onOutputChange }) => {
  const [input, setInput] = useState('');
  const [isCursorHidden, setIsCursorHidden] = useState(false);
  const inputRef = useRef(null);
  

  const debouncedOutputChange = useCallback(debounce(onOutputChange, 50), [onOutputChange]);

  useEffect(() => {
    if (inputValue != null && inputValue.toString() !== input) {
      setInput(inputValue.toString());
    }
  }, [inputValue]);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 200);
    return () => clearTimeout(t);
  }, []);

  // Function to handle decimal points within segments
  const sanitizeDecimalPoints = (expr) => {
    const tokens = expr.split(/([+\-*/])/);
    for (let i = 0; i < tokens.length; i++) {
      if (!isOperator(tokens[i]) && (tokens[i].match(/\./g) || []).length > 1) {
        // Keep only the first decimal point in each segment
        tokens[i] = tokens[i].replace(/\.(?=.*\.)/g, '');
      }
    }
    return tokens.join('');
  };

  // Function to format expression for evaluation
  const formatForEvaluation = (expr) => {
    // Replace multiple leading zeros before evaluation
    return expr.replace(/\b0+(\d)/g, '$1');
  };

  const sanitizeInput = (expr) => {
    // Remove consecutive operators
    expr = expr.replace(/([+\-*/]){2,}/g, (match) => match.slice(-1));

    // Prevent multiple dots in the same number segment
    expr = sanitizeDecimalPoints(expr);

    return expr;
  };

  const isValidExpression = (expr) => {
    const lastChar = expr.slice(-1);
    return expr && !isOperator(lastChar) && lastChar !== '.';
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    
    // Filter non-digit/operator characters
    const filteredVal = val.replace(/[^0-9+\-*/.]/g, '');
    
    // Handle empty input or just a dot
    if (filteredVal === '.') {
      setInput('0.');
      debouncedOutputChange('0.');
      return;
    }
    
    // Sanitize the input to handle decimal points and consecutive operators
    const sanitized = sanitizeInput(filteredVal);
    // const normalizedOutput = sanitized.replace(/^0+/, '') || '0';
    setInput(sanitized);
    debouncedOutputChange(sanitized);
    
    // Explicitly show cursor when typing via keyboard
    setIsCursorHidden(false);
  };

  // Simple function to insert character at cursor position
  const insertAtCursor = (char, cursorOffset = 1) => {
    if (!inputRef.current) return;
  
    const cursorPosition = inputRef.current.selectionStart || input.length;
    const newValue = input.slice(0, cursorPosition) + char + input.slice(cursorPosition);
  
    setInput(newValue);
    debouncedOutputChange(newValue);
  
    // Set cursor position immediately without setTimeout
    const newCursorPosition = cursorPosition + cursorOffset;
    if (inputRef.current) {
      inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
    }
  };
  const handleButtonClick = (btn) => {
    let expr = input;

    // Only hide cursor for mouse-initiated button clicks
    setIsCursorHidden(true);

    switch (btn) {
      case 'AC':
        expr = '';
        setInput(expr);
        debouncedOutputChange(expr);
        break;
      case 'C':
        expr = expr.slice(0, -1);
        setInput(expr);
        debouncedOutputChange(expr.replace(/^0+/, '') || '0');
        break;
      case '=':
        if (!isValidExpression(expr)) return;
        try {
          // Format expression before evaluation (remove leading zeros)
          const formattedExpr = formatForEvaluation(expr);
          const result = eval(formattedExpr) + '';
          expr = isFinite(result) ? result : '0';
          setInput(expr);
          debouncedOutputChange(expr);
        } catch {
          expr = '0';
          setInput(expr);
          debouncedOutputChange(expr);
        }
        break;
      case '.':
        // Check if we need to prevent adding a decimal point
        const segments = expr.split(/[+\-*/]/);
        const lastSegment = segments[segments.length - 1];
        
        if (lastSegment.includes('.')) {
          // If the current segment already has a decimal point, don't add another
          return;
        }
        
        if (expr === '' || isOperator(expr.slice(-1))) {
          insertAtCursor('0.', 2);
        } else {
          insertAtCursor('.');
        }
        break;
      default:
        if (['%', 'x²', '1/x', '√x'].includes(btn)) {
          if (!isValidExpression(expr)) return;
          try {
            // Format expression before evaluation
            const formattedExpr = formatForEvaluation(expr);
            const val = eval(formattedExpr);
            let out;
            switch (btn) {
              case '%': out = val / 100; break;
              case 'x²': out = val * val; break;
              case '1/x': out = 1 / val; break;
              case '√x': out = Math.sqrt(val); break;
            }
            expr = isFinite(out) ? out + '' : '0';
            setInput(expr);
            debouncedOutputChange(expr);
          } catch {
            expr = '0';
            setInput(expr);
            debouncedOutputChange(expr);
          }
        } else if (isOperator(btn)) {
          // Handle operators
          if (expr === '' && btn === '-') {
            insertAtCursor('-'); // Allow negative at start
          } else if (expr !== '' && !isOperator(expr.slice(-1))) {
            insertAtCursor(btn);
          } else if (isOperator(expr.slice(-1))) {
            // Replace the last operator
            expr = expr.slice(0, -1) + btn;
            setInput(expr);
            debouncedOutputChange(expr.replace(/^0+/, '') || '0');
          }
        } else {
          // Handle numbers
          insertAtCursor(btn);
        }
        break;
    }

    // Maintain focus but keep cursor hidden
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleButtonClick('=');
    }
    else if(e.key === 'ArrowRight' || e.key === 'ArrowLeft'){
      e.stopPropagation()
    }
     else if (/^[0-9+\-*/.%]$/.test(e.key)) {
      e.preventDefault();
      
      // Don't hide cursor when typing via keyboard
      const tempInput = input;
      
      if (e.key === '.') {
        handleButtonClick('.');
      } else {
        handleButtonClick(e.key);
      }

   
      
      // Explicitly show cursor after button handling
      setIsCursorHidden(false);
    }
  };

  const handleFocus = () => {
    // Don't interfere with cursor position when using buttons
    // The insertAtCursor function will handle positioning correctly
  };

  return (
    <div className="calculator">
      <input
        className={`display ${isCursorHidden ? 'cursor-hidden' : ''}`}
        ref={inputRef}
        type="text"
        value={input}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onClick={() => setIsCursorHidden(false)} // Show cursor when user clicks in the input
        onPaste={(event) => {
          event.preventDefault();
          const pastedData = event.clipboardData.getData('text');
        
          // Filter to basic allowed characters (you already do this)
          const filtered = pastedData.replace(/[^0-9+\-*/.]/g, '');
        
          // Sanitize it using the same function used for typing
          const sanitized = sanitizeInput(filtered);
        
          // Only accept the paste if it doesn't change during sanitization
          if (sanitized === filtered) {
            setInput(sanitized);
            debouncedOutputChange(sanitized);
          } else {
            // Optionally show error or ignore
            console.warn("Invalid paste rejected:", pastedData);
          }
          
          // Show cursor when pasting
          setIsCursorHidden(false);
        }}
        placeholder="0"
      />
      <div className="buttons">
        {[
          'AC', 'C', 'x²', '1/x',
          '√x', '%', '/', '*',
          '7', '8', '9', '-',
          '4', '5', '6', '+',
          '1', '2', '3', '.', '0', '='
        ].map((btn) => (
          <button
            key={btn}
            className={['AC', 'C', '=', 'x²', '1/x', '√x', '%', '/', '*', '-', '+'].includes(btn) ? 'expression-button' : ''}
            onClick={() => handleButtonClick(btn)}
          >
            {btn}
          </button>
        ))}
      </div>
    </div>
  );
};

export default Calculator;